import {
  BffBonusCreditPathParams,
  BffBonusForfeitPathParams,
  BffFavouriteGamesAddPathParams,
  BffKycFilesUploadPathParams,
  BffSetAuthenticationMethodPathParams,
  BffSubdivisionsPerCountryCodePathParams,
  BffTotalJackpotPerCurrencyPathParams,
  BffGetIdScanStatusPathParams,
} from '@lucky7ventures/bff-types';

export const BFF_API_URLS = {
  // ADDRESS
  ADDRESS_LOOKUP: '/api/v1/address/lookup',
  ADDRESS_RETRIEVAL: '/api/v1/address/retrieve',

  // AUTH
  CHANGE_PASSWORD: '/api/v1/auth/change-password',
  FORGOT_PASSWORD: '/api/v1/auth/forgot-password',
  FORGOT_PASSWORD_CHANGE: '/api/v1/auth/forgot-password/change',
  FORGOT_PASSWORD_SMS: '/api/v1/auth/forgot-password-sms',
  FORGOT_PASSWORD_SMS_CHANGE: '/api/v1/auth/forgot-password-sms/change',
  LOGOUT: '/api/v1/auth/logout',
  LOGIN_OTP: '/api/v1/auth/login-otp',
  AUTHENTICATE_WITH_CHALLENGE: '/api/v1/auth/challenge',
  LOGIN: '/api/v1/auth/login',
  CHECK_SESSION: '/api/v1/auth/session',

  // DATA
  GET_COUNTRIES: '/api/v1/data/countries',
  FETCH_TOTAL_JACKPOT: ({ currency }: BffTotalJackpotPerCurrencyPathParams) =>
    `/api/v1/data/total-jackpot/${currency}`,
  GET_SUBDIVISIONS: ({ countryCode }: BffSubdivisionsPerCountryCodePathParams) =>
    `/api/v1/data/subdivisions/${countryCode}`,

  // REGISTER
  REGISTER_MEXICO_2: '/api/v1/register/mx-2',
  REGISTER_AGCO: '/api/v1/register/v2/agco',
  REGISTER_THAILAND: '/api/v1/register/th',
  REGISTER_FAST: '/api/v1/register/fast',
  REGISTER_MOBILE_NUMBER_VERIFICATION_REQUIRED:
    '/api/v1/register/mobile-number-verification-required',

  // RESPONSIBLE-GAMING
  ADD_TIMEOUT: '/api/v1/responsible-gaming/timeout',
  GET_REALITY_CHECK: '/api/v1/responsible-gaming/reality-check',
  SET_USER_LIMITS: '/api/v1/responsible-gaming/limits',
  SET_REALITY_CHECK: '/api/v1/responsible-gaming/reality-check',
  ADD_PANIC_TIMEOUT: '/api/v1/responsible-gaming/timeout/panic',
  ADD_SELF_EXCLUSION: '/api/v1/responsible-gaming/self-exclusion',
  LIMITS_REFUND: '/api/v1/responsible-gaming/limits/refund',

  // USER
  COMPLETE_PROFILE: '/api/v1/user/complete-profile',
  COMPLETE_PROFILE_MEXICO: '/api/v1/user/complete-profile/mexico',
  COMPLETE_PROFILE_MEXICO_2: '/api/v1/user/complete-profile/mx-2',
  COMPLETE_PROFILE_MEXICO_3: '/api/v1/user/complete-profile/v3/mexico',
  COMPLETE_PROFILE_THAILAND: '/api/v1/user/complete-profile/th',
  COMPLIANCE_CHECK_ONTARIO: '/api/v1/user/compliance/ontario',
  CREDIT_BONUS: ({ promoCode }: BffBonusCreditPathParams) => `/api/v1/user/bonus/${promoCode}`,
  FORFEIT_BONUS: ({ userBonusId }: BffBonusForfeitPathParams) =>
    `/api/v1/user/bonus/${userBonusId}`,
  GENERATE_ID_SCAN_DATA: '/api/v1/user/generate-id-scan-data',
  START_CHECKIN_ID_SCAN: '/api/v1/user/start-checkin-id-scan',
  GENERATE_STATUS_TOKEN: '/api/v1/user/generate-status-token',
  GET_AUTHENTICATION_METHOD: '/api/v1/user/get-auth-method',
  GET_BONUSES: '/api/v1/user/bonuses',
  GET_KYC: '/api/v1/user/kyc',
  GET_LIFETIME_DEPOSITS: '/api/v1/user/lifetime-deposits',
  GET_WALLET: '/api/v1/user/wallet',
  KYC_DOCUMENTS_UPLOAD: ({ kycType }: BffKycFilesUploadPathParams) =>
    `/api/v1/user/kyc/${kycType}/upload`,
  KYC_SOWQ: '/api/v1/user/kyc/sowq',
  SET_AUTHENTICATION_METHOD: ({ authMethod }: BffSetAuthenticationMethodPathParams) =>
    `/api/v1/user/set-auth-method/${authMethod}`,
  VERIFY: '/api/v1/user/verify',
  FETCH_LAST_SUCCESSFUL_DEPOSIT: '/api/v1/user/wallet/last-successful-deposit',
  GET_CONSENTS: '/api/v1/user/consents',
  UPDATE_CONSENTS: '/api/v1/user/consents',
  GET_ID_SCAN_STATUS: ({ journeyRef }: BffGetIdScanStatusPathParams) =>
    `/api/v1/user/id-scan-status/${journeyRef}`,
  GET_LOGIN_HISTORY: '/api/v1/user/login-history',
  GET_USER_TAGS: '/api/v1/user/tags',
  GET_USER: '/api/v1/user/self',
  GET_USER_PLAY_STATS: '/api/v1/user/play-stats',
  GET_USER_AML_VERIFICATION_STATUS: '/api/v1/user/aml-verification-status',
  GET_AGCO_USER_DETAILS: '/api/v1/user/agco',
  AGCO_USER_VERIFY: '/api/v1/user/agco/verify',
  AGCO_USER_VERIFY_UPDATE: '/api/v1/user/agco/verify-update',
  GET_USER_LIMITS: '/api/v1/user/limits',
  GET_WALLET_TRANSACTIONS: '/api/v1/user/wallet/transactions',
  GET_USER_GRACE_PERIOD: '/api/v1/user/grace-period',
  UPDATE_USER: '/api/v1/user',
  CHANGE_USER_EMAIL: '/api/v1/user/email',
  UPDATE_CONTACT_INFO: '/api/v1/user/contact-info',

  // GAMES
  GET_ALL_GAMES: '/api/v1/games/fetch',
  GET_FAVOURITE_GAMES: '/api/v1/games/favourites',
  FAVOURITE_GAME: ({ gameId }: BffFavouriteGamesAddPathParams) =>
    `/api/v1/games/favourites/add/${gameId}`,
  REMOVE_FROM_FAVOURITE_GAMES: ({ gameId }: BffFavouriteGamesAddPathParams) =>
    `/api/v1/games/favourites/delete/${gameId}`,
  GET_GAME_URL: '/api/v1/games/game/url',
  GET_GAMES_JACKPOTS: '/api/v1/games/jackpots',
  GET_LAST_PLAYED: '/api/v1/games/last-played',
  FETCH_GAMES_TRANSACTIONS: '/api/v1/games/transactions',

  // VALIDATE
  VALIDATE_EMAIL_PHONE: '/api/v1/validate/email-phone',

  // COMPLIANCE
  ONTARIO_CHECK: '/api/v1/compliance/ontario-check',
  REGION_CHECK: '/api/v1/compliance/region-check',

  // DATA
  FETCH_INDUSTRIES_AND_OCCUPATIONS: '/api/v1/data/industries-and-occupations',

  // CMS
  GET_BONUS_INTRO_CARDS: '/cms/bonus-intro-cards',
  GET_FAQ: '/cms/faq',
  GET_PROMOTIONS: '/cms/promotions',
  GET_PSA: '/cms/psa',
  GET_STATIC_TEXT_CONTENT: '/cms/static',
  GET_WELCOME_TEXTS: '/cms/welcome-texts',
  GET_HEADER_BANNERS: '/cms/header-banners',
  GET_JACKPOT: '/cms/jackpot',
  GET_HERO_BACKGROUNDS: '/cms/hero-backgrounds',

  // FRAUD
  SEON_FRAUD_EVENT: '/api/v1/fraud/seon',

  // CONNECTION
  SSE_DISCONNECT_CLIENT: '/connection/sse/disconnect-client',
};
